import useSWR from 'swr'
import { APIDecoratorWithBaseURI } from '../../Service/api'

// OP-3403 — Validador de Ingressos. Consome o BFF em bd-services-producer-analytics.
const BASE = 'producer/analytics/ticketValidator'

export interface Occasion {
  numero: number
  nome: string
}

export type TicketStatus = 'DISPONIVEL' | 'UTILIZADO' | 'CANCELADO'

export interface SearchTicket {
  bilheteId: number
  chave: string
  nome: string
  ingresso: string
  setor: string
  situacao: string
  status: TicketStatus
}

export interface TicketInfo {
  chave: string
  nome: string
  ingresso: string
  setor?: string
  lugar?: string | null
  ocasiao: number | string
}

export type ValidationMotivo =
  | 'NOT_FOUND'
  | 'CANCELLED'
  | 'USED'
  | 'BLOCKED'
  | 'LOCAL'
  | null

export interface SectorTicket {
  ingressoId: number
  ingresso: string
}

export interface Sector {
  setorId: number
  setor: string
  ingressos: SectorTicket[]
}

export interface EventGroup {
  eventoId: number
  evento: string
  setores: Sector[]
}

export interface ValidationResult {
  success: boolean
  valido: boolean
  motivo: ValidationMotivo
  mensagem?: string
  horario?: string
  validadoEm?: string
  deviceId?: string
  deviceToken?: string
  ingresso?: TicketInfo
}

export interface HistoryItem {
  horario: string
  dataHora: string
  chave: string
  ingresso: string
  setor: string
  nome: string | null
  ocasiao: number
  origem: 'dashboard' | 'app'
}

// Token de dispositivo assinado pelo servidor. O cliente apenas guarda e
// reenvia; o servidor gera/valida (HMAC) — não é forjável no cliente.
const DEVICE_TOKEN_KEY = 'validator-device-token'

export const loadDeviceToken = (): string | null => {
  try {
    return window.localStorage.getItem(DEVICE_TOKEN_KEY)
  } catch {
    return null
  }
}

export const saveDeviceToken = (token: string): void => {
  try {
    window.localStorage.setItem(DEVICE_TOKEN_KEY, token)
  } catch {
    // ignore
  }
}

// Rótulo curto do dispositivo (uuid antes do ponto do token).
export const deviceLabelFromToken = (): string => {
  const token = loadDeviceToken()
  const uuid = token ? token.split('.')[0] : ''
  return uuid ? `DASH·${uuid.slice(0, 8)}` : 'DASH·—'
}

// Seleção de ingressos a validar, por evento e por dispositivo (localStorage).
const selectionKey = (eventId: string) => `validator-selection-${eventId}`

export const loadSelection = (eventId: string): number[] | null => {
  try {
    const raw = window.localStorage.getItem(selectionKey(eventId))
    if (!raw) return null
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr.map(Number) : null
  } catch {
    return null
  }
}

export const saveSelection = (eventId: string, ids: number[]): void => {
  try {
    window.localStorage.setItem(selectionKey(eventId), JSON.stringify(ids))
  } catch {
    // ignore
  }
}

// SWR — ocasiões do evento (somente-leitura, cadastradas no P1).
export const useOccasions = (eventId?: string) => {
  const { data, error, isLoading } = useSWR(
    eventId ? [`${BASE}/occasions`, eventId] : null,
    async () => {
      const res = await APIDecoratorWithBaseURI().get(`${BASE}/occasions`, {
        params: { eventId },
      })
      return (res.data?.ocasioes ?? []) as Occasion[]
    }
  )
  return {
    occasions: data ?? [],
    loadingOccasions: isLoading,
    occasionsError: error,
  }
}

// SWR — últimos validados.
export const useValidationHistory = (eventId?: string, limit = 20) => {
  const { data, error, isLoading, mutate } = useSWR(
    eventId ? [`${BASE}/history`, eventId, limit] : null,
    async () => {
      const res = await APIDecoratorWithBaseURI().get(`${BASE}/history`, {
        params: { eventId, limit },
      })
      return (res.data?.validados ?? []) as HistoryItem[]
    }
  )
  return {
    history: data ?? [],
    loadingHistory: isLoading,
    historyError: error,
    refreshHistory: mutate,
  }
}

// SWR — eventos (próprio + pacote) > setores > ingressos (para o filtro).
export const useSectors = (eventId?: string) => {
  const { data, error, isLoading } = useSWR(
    eventId ? [`${BASE}/sectors`, eventId] : null,
    async () => {
      const res = await APIDecoratorWithBaseURI().get(`${BASE}/sectors`, {
        params: { eventId },
      })
      return (res.data?.eventos ?? []) as EventGroup[]
    }
  )
  return { eventos: data ?? [], loadingSectors: isLoading, sectorsError: error }
}

// Busca auto-classificada (CPF/Pedido/Nome/E-mail/Bilhete).
export const searchTickets = async (
  eventId: string,
  q: string
): Promise<{
  tipoDetectado: string
  ingressos: SearchTicket[]
  hasMore: boolean
}> => {
  const res = await APIDecoratorWithBaseURI().get(`${BASE}/search`, {
    params: { eventId, q },
  })
  return {
    tipoDetectado: res.data?.tipoDetectado ?? '',
    ingressos: (res.data?.ingressos ?? []) as SearchTicket[],
    hasMore: Boolean(res.data?.hasMore),
  }
}

// Valida o ingresso (decide + grava). O BFF orquestra o validador-api.
export const validateTicket = async (
  eventId: string,
  code: string,
  ocasiao: number,
  ingressosPermitidos?: number[] | null
): Promise<ValidationResult> => {
  const body: Record<string, any> = {
    eventId,
    code,
    ocasiao,
  }
  // array (mesmo vazio) = aplica filtro; ausente = valida tudo
  if (Array.isArray(ingressosPermitidos)) {
    body.ingressosPermitidos = ingressosPermitidos
  }
  const token = loadDeviceToken()
  if (token) body.deviceToken = token

  const res = await APIDecoratorWithBaseURI().post(`${BASE}/validate`, body)
  const data = res.data as ValidationResult

  // Persiste o token (emitido/renovado pelo servidor) para manter o dispositivo.
  if (data?.deviceToken) saveDeviceToken(data.deviceToken)
  return data
}
